import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  Grid2X2,
  Loader2,
  Minus,
  Plus,
  RefreshCw,
  Search,
  Trash2,
  X,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { useAccountSummary } from "../../hooks/useAccountData";
import { useProductTradability } from "../../hooks/useProductTradability";
import investorStrategyService, { type StrategyEntry } from "../../services/investorStrategyService";
import configService from "../../services/configService";
import type { UserSettingsMap } from "../../services/profileConfigService";
import type { ProductSearchItem } from "../../types/productV3";
import { hasAcknowledgedAllocationMethodology } from "../../utils/cashDeploymentSettings";
import {
  formatCurrencyWithCommas,
  formatWeight,
  roundToDecimals,
  safeParseFloat,
} from "../../utils/numberUtils";
import {
  getManagedStrategySeedWeight,
  getStrategyAllocationLimitForAccount,
} from "../../utils/strategyAllocationLimits";
import { weightToWholeDollars } from "../../utils/strategyDollarInputMode";
import {
  computeRestrictedNewProducts,
  getStrategyDisplayName,
  getMinNlvShortfallWarning,
  type ProductLookupKey,
  type RestrictedNewProduct,
  type StrategyDraftEntry,
} from "../../utils/strategyUtils";
import AllocationMethodologyAckModal from "../dashboard/components/AllocationMethodologyAckModal";
import ProductTradabilityModal from "../dashboard/components/ProductTradabilityModal";
import {
  normalizeAccountNumber,
  USD_NULL_DISPLAY,
} from "../../components/portfolio/columns/formatters";
import {
  PLUTUS_MANAGED_LABEL,
  useAlphaAllocationDraftContext,
  type AlphaAllocationRow,
} from "../../contexts/AlphaAllocationDraftContext";
import type { AlphaIbkrAccountType } from "./alphaIbkrAccountDetails";
import { isAlphaDevPreviewEnvironment } from "./alphaDevPreview";
import AlphaManagedNudge from "./AlphaManagedNudge";
import AlphaManagedPreviewModal from "./AlphaManagedPreviewModal";
import { useAlphaManagedSizing } from "./useAlphaManagedSizing";

export interface AlphaAllocationsModalProps {
  open: boolean;
  effectiveAccountType: AlphaIbkrAccountType | null;
  onClose: () => void;
  /** Fired after a successful apply so the dashboard can refetch allocations. */
  onSaved?: () => void;
}

type EntityChipType = AlphaAllocationRow["entityType"];

const CHIP_LABEL: Record<EntityChipType, string> = {
  MANAGED_STRATEGY: "Managed",
  PORTFOLIO: "Portfolio",
  BASKET: "Basket",
};

// Mobile and tablet stay compact. Desktop adds one read-only dollar column
// while keeping percentage as the only editable allocation value.
const ALLOC_GRID =
  "lg:grid lg:grid-cols-[minmax(0,1fr)_minmax(8rem,0.24fr)_minmax(12rem,0.34fr)_2.75rem]";

const ALLOCATION_PROGRESS_CLASSES = [
  "bg-v7-bronze-dark",
  "bg-v7-success",
  "bg-v7-rust",
  "bg-v7-ink",
  "bg-v7-bronze",
  "bg-v7-ink-soft",
] as const;

const DEV_MANAGED_FIXTURE_ACCOUNT_VALUE = 250_000;
const DEV_MANAGED_FIXTURE_STRATEGY = "Balanced Growth";
const DEV_MANAGED_FIXTURE_ROWS: AlphaAllocationRow[] = [
  {
    entityType: "MANAGED_STRATEGY",
    productId: 900_001,
    weight: 35,
    currentWeight: 34.6,
    displayName: PLUTUS_MANAGED_LABEL,
  },
  {
    entityType: "PORTFOLIO",
    productId: 900_002,
    weight: 40,
    currentWeight: 40.2,
    displayName: "US Quality Leaders",
  },
  {
    entityType: "BASKET",
    productId: 900_003,
    weight: 15,
    currentWeight: 14.8,
    displayName: "Global Income Diversifier",
  },
];

function chipClass(entityType: EntityChipType): string {
  if (entityType === "MANAGED_STRATEGY") {
    return "border-v7-bronze-dark/35 bg-v7-bronze/10 text-v7-bronze-dark";
  }
  return "border-v7-ink/18 bg-v7-ink/[0.04] text-v7-ink-soft";
}

// Provider logo with a monogram fallback, mirroring the dashboard settings
// avatar. Keeps the catalog rows visually richer than plain text.
const ProviderLogo: React.FC<{
  providerId: number | null;
  providerName: string;
  srcOverride?: string;
  testId?: string;
}> = ({
  providerId,
  providerName,
  srcOverride,
  testId,
}) => {
  const [failed, setFailed] = useState(false);
  const initial = providerName.trim().slice(0, 1).toUpperCase() || "?";
  const src = srcOverride
    ?? (providerId != null && providerId > 0
      ? `/provider-assets/${providerId}/logo.png`
      : null);
  if (failed || src == null) {
    return (
      <span
        data-testid={testId}
        aria-label={`${providerName} logo unavailable`}
        className="grid h-8 w-8 shrink-0 place-items-center overflow-hidden rounded-v7-control border border-v7-ink/10 bg-v7-paper text-xs font-semibold text-v7-ink-soft"
      >
        {initial}
      </span>
    );
  }
  return (
    <img
      data-testid={testId}
      src={src}
      alt={`${providerName} logo`}
      onError={() => setFailed(true)}
      className="h-8 w-8 shrink-0 rounded-v7-control border border-v7-ink/10 object-cover"
    />
  );
};

interface AllocationWeightControlProps {
  label: string;
  value: number;
  disabled: boolean;
  onChange: (value: number) => void;
}

const AllocationWeightControl: React.FC<AllocationWeightControlProps> = ({
  label,
  value,
  disabled,
  onChange,
}) => {
  const [draft, setDraft] = useState<string | null>(null);

  const commitDraft = () => {
    if (draft == null) return;
    const parsed = Number(draft);
    const next = draft !== "" && draft !== "." && Number.isFinite(parsed) ? parsed : 0;
    onChange(roundToDecimals(next));
    setDraft(null);
  };

  const stepBy = (amount: number) => {
    setDraft(null);
    onChange(Math.max(0, Math.round(value) + amount));
  };

  return (
    <span className="inline-flex items-center overflow-hidden rounded-v7-control border border-v7-ink/20 bg-v7-white">
      <button
        type="button"
        aria-label={`Decrease ${label}`}
        disabled={disabled}
        onClick={() => stepBy(-5)}
        className="grid h-11 w-7 place-items-center text-v7-ink-soft transition-colors hover:bg-v7-paper disabled:cursor-not-allowed disabled:text-v7-ink/30 lg:w-9"
      >
        <Minus className="h-3.5 w-3.5" aria-hidden="true" />
      </button>
      <input
        aria-label={`${label} allocated percent`}
        disabled={disabled}
        value={draft ?? formatWeight(value)}
        onFocus={() => setDraft(formatWeight(value))}
        onChange={(event) => {
          const next = event.currentTarget.value;
          if (!/^\d*(?:\.\d*)?$/.test(next)) return;
          setDraft(next);
          if (next === "" || next === ".") return;
          const parsed = Number(next);
          if (Number.isFinite(parsed)) onChange(roundToDecimals(parsed));
        }}
        onBlur={commitDraft}
        className="h-11 w-12 border-x-0 bg-v7-white text-center font-v7-serif text-sm text-v7-ink outline-none focus:ring-1 focus:ring-inset focus:ring-v7-bronze-dark disabled:cursor-not-allowed disabled:text-v7-ink/45 lg:w-16 lg:text-base"
        inputMode="decimal"
      />
      <button
        type="button"
        aria-label={`Increase ${label}`}
        disabled={disabled}
        onClick={() => stepBy(5)}
        className="grid h-11 w-7 place-items-center text-v7-ink-soft transition-colors hover:bg-v7-paper disabled:cursor-not-allowed disabled:text-v7-ink/30 lg:w-9"
      >
        <Plus className="h-3.5 w-3.5" aria-hidden="true" />
      </button>
    </span>
  );
};

interface DiffRow {
  key: string;
  displayName: string;
  from: number;
  to: number;
  kind: "added" | "removed" | "modified";
}

// Managed rows collapse to one logical "Plutus Managed" entry for the diff. A
// mix rematch swaps the row's productId, and a productId-keyed diff would then
// render one removed and one added row, both named "Plutus Managed".
const MANAGED_DIFF_KEY = "MANAGED_STRATEGY-0";

function normalizeManagedForDiff(list: AlphaAllocationRow[]): AlphaAllocationRow[] {
  const managed = list.filter((row) => row.entityType === "MANAGED_STRATEGY");
  const rest = list.filter((row) => row.entityType !== "MANAGED_STRATEGY");
  if (managed.length === 0) return rest;
  if (managed.length > 1) return list;
  return [
    {
      entityType: "MANAGED_STRATEGY",
      productId: 0,
      weight: managed.reduce((sum, row) => sum + row.weight, 0),
      currentWeight: null,
      displayName: PLUTUS_MANAGED_LABEL,
    },
    ...rest,
  ];
}

function computeAllocationDiff(
  rows: AlphaAllocationRow[],
  savedRows: AlphaAllocationRow[]
): DiffRow[] {
  const savedByKey = new Map(savedRows.map((r) => [`${r.entityType}-${r.productId}`, r]));
  const nextByKey = new Map(
    rows.filter((r) => r.weight > 0).map((r) => [`${r.entityType}-${r.productId}`, r])
  );
  const keys = new Set<string>([...savedByKey.keys(), ...nextByKey.keys()]);
  const diffs: DiffRow[] = [];
  keys.forEach((key) => {
    const before = savedByKey.get(key);
    const after = nextByKey.get(key);
    const from = before?.weight ?? 0;
    const to = after?.weight ?? 0;
    if (from === to) return;
    diffs.push({
      key,
      displayName: after?.displayName ?? before?.displayName ?? key,
      from,
      to,
      kind: before && after ? "modified" : after ? "added" : "removed",
    });
  });
  return diffs;
}

const AlphaAllocationsModal: React.FC<AlphaAllocationsModalProps> = ({
  open,
  effectiveAccountType,
  onClose,
  onSaved,
}) => {
  const { getUserConfigValue, userConfig, userConfigLoaded } = useAuth();
  const isCustomer = getUserConfigValue("iscustomer") === "true";
  const hasIbkrAccount = !userConfigLoaded || Boolean(userConfig?.ibkrAccountId);
  const { data: accountSummary, isLoading: isLoadingAccount } = useAccountSummary(
    undefined,
    hasIbkrAccount && open
  );
  const rawNlv = accountSummary?.netLiquidationSecurity;
  const nlv = rawNlv != null ? safeParseFloat(rawNlv) : null;
  const accountValue = normalizeAccountNumber(rawNlv);
  const isDevManagedFixtureAvailable = isAlphaDevPreviewEnvironment(
    configService.getEnvironment()
  );
  const { customerCountry } = useProductTradability();

  // First-save allocation-methodology gate: shown once per user (before the
  // first save while unacknowledged), read from data.userSettings. Shares the
  // same auto_deploy_clarification flag as the legacy flow, so an ack made in
  // either place counts for both.
  const userSettings = useMemo<UserSettingsMap | undefined>(() => {
    const raw = userConfig?.data?.userSettings;
    return raw && typeof raw === "object" ? (raw as UserSettingsMap) : undefined;
  }, [userConfig?.data]);
  const hasAcknowledgedAllocation = hasAcknowledgedAllocationMethodology(userSettings);

  // Read the shared alpha draft, and load it when the editor opens (Discover
  // may already have triggered the load; __triggerInit is idempotent).
  const draft = useAlphaAllocationDraftContext();
  const { __triggerInit: triggerDraftInit } = draft;
  useEffect(() => {
    if (open) triggerDraftInit();
  }, [open, triggerDraftInit]);
  const {
    status,
    rows,
    savedRows,
    totalWeight,
    isDirty,
    hasPendingChanges,
    minNlvOf,
    addRow,
    adoptMix,
    removeRow,
    setWeight,
    swapManagedProduct,
    isInDraft,
    resetToSaved,
    markSaved,
    invalidate,
    reload,
    catalog,
    productLookup,
  } = draft;

  const handleAdoptManaged = useCallback((managedProductId: number) => {
    if (effectiveAccountType === null) return;
    adoptMix([{
      entityType: "MANAGED_STRATEGY",
      productId: managedProductId,
      weight: getManagedStrategySeedWeight(effectiveAccountType === "retirement"),
      displayName: PLUTUS_MANAGED_LABEL,
    }]);
  }, [adoptMix, effectiveAccountType]);

  const allocationLimit = useMemo(
    () => effectiveAccountType === null
      ? null
      : getStrategyAllocationLimitForAccount(
        userConfig?.data,
        effectiveAccountType === "retirement"
      ),
    [effectiveAccountType, userConfig?.data]
  );

  const managedRows = useMemo(
    () => rows.filter((row) => row.entityType === "MANAGED_STRATEGY"),
    [rows]
  );
  const savedManagedRows = useMemo(
    () => savedRows.filter((row) => row.entityType === "MANAGED_STRATEGY"),
    [savedRows]
  );
  const hasMultipleManagedRows =
    managedRows.length > 1 || savedManagedRows.length > 1;

  // A restored draft can predate a saved Managed row. In the fail-closed state,
  // show and project a keyed union without changing the persisted draft.
  const readOnlyRows = useMemo(() => {
    if (!hasMultipleManagedRows) return rows;
    const keys = new Set(rows.map((row) => `${row.entityType}-${row.productId}`));
    return [
      ...rows,
      ...savedRows.filter((row) => !keys.has(`${row.entityType}-${row.productId}`)),
    ];
  }, [hasMultipleManagedRows, rows, savedRows]);
  const liveDisplayedTotalWeight = hasMultipleManagedRows
    ? readOnlyRows.reduce((sum, row) => sum + row.weight, 0)
    : totalWeight;

  // Sizing and rematching are singleton operations. Multiple managed rows are
  // rendered read-only below rather than silently choosing one.
  const managedRow = useMemo(
    () => !hasMultipleManagedRows && managedRows.length === 1 ? managedRows[0] : null,
    [hasMultipleManagedRows, managedRows]
  );
  const savedManagedRow = useMemo(
    () => !hasMultipleManagedRows && savedManagedRows.length === 1 ? savedManagedRows[0] : null,
    [hasMultipleManagedRows, savedManagedRows]
  );

  const [query, setQuery] = useState("");
  const [isBrowseOpen, setIsBrowseOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showManagedPreview, setShowManagedPreview] = useState(false);
  const [showManagedFixture, setShowManagedFixture] = useState(false);
  const [showAckGate, setShowAckGate] = useState(false);
  const [restrictedDetails, setRestrictedDetails] = useState<RestrictedNewProduct[]>([]);

  const fixtureManagedRow = DEV_MANAGED_FIXTURE_ROWS[0];
  const displayedRows = showManagedFixture ? DEV_MANAGED_FIXTURE_ROWS : readOnlyRows;
  const displayedTotalWeight = showManagedFixture
    ? DEV_MANAGED_FIXTURE_ROWS.reduce((sum, row) => sum + row.weight, 0)
    : liveDisplayedTotalWeight;
  const displayedAccountValue = showManagedFixture
    ? DEV_MANAGED_FIXTURE_ACCOUNT_VALUE
    : accountValue;
  const displayedManagedRow = showManagedFixture ? fixtureManagedRow : managedRow;

  // Reset transient UI whenever the modal reopens.
  useEffect(() => {
    if (open) return;
    setQuery("");
    setIsBrowseOpen(false);
    setSaveError(null);
    setShowConfirm(false);
    setShowAckGate(false);
    setRestrictedDetails([]);
    setIsSaving(false);
    setShowManagedPreview(false);
    setShowManagedFixture(false);
  }, [open]);

  useEffect(() => {
    if (!hasMultipleManagedRows) return;
    setShowConfirm(false);
    setShowManagedPreview(false);
  }, [hasMultipleManagedRows]);

  // Managed sizing engine: floor gate, live mix resolution (with in-place
  // product swap on a band cross), and the informational perf load. The same
  // config-first, questionnaire-second account type drives both the
  // allocation cap and managed risk-track resolution.
  const managedSizing = useAlphaManagedSizing({
    open: open && !hasMultipleManagedRows,
    isCustomer,
    nlv,
    configAccountType: effectiveAccountType,
    managedProductId: managedRow?.productId ?? null,
    managedWeight: managedRow?.weight ?? null,
    savedManagedProductId: savedManagedRow?.productId ?? null,
    savedManagedWeight: savedManagedRow?.weight ?? null,
    onSwapProduct: swapManagedProduct,
  });

  // The sizing load is keyed to the selected Managed product and already
  // carries its validated composition. Resolve display names from the catalog
  // already loaded for this editor; no extra lookup or matching path is needed.
  const managedConstituents = useMemo(() => {
    const performance = managedSizing.perf.data;
    if (!performance || performance.managedStrategyId !== managedRow?.productId) {
      return null;
    }
    return performance.composition.map((entry) => ({
      entityType: entry.entityType,
      productId: entry.productId,
      name: getStrategyDisplayName(entry, productLookup),
      weightPct: entry.weightPct,
    }));
  }, [managedSizing.perf.data, managedRow?.productId, productLookup]);

  // Browse-all + search both filter the full catalog locally (mirrors the
  // analyzer's browse modal). The dropdown opens when browsing or once a query
  // is typed; the catalog already excludes hidden products.
  const trimmedQuery = query.trim().toLowerCase();
  const isPickerOpen = isBrowseOpen || trimmedQuery.length >= 1;
  const catalogResults = useMemo(() => {
    if (!isPickerOpen) return [];
    const matches = trimmedQuery.length === 0
      ? catalog
      : catalog.filter((item) => (
        `${item.title} ${item.providerName}`.toLowerCase().includes(trimmedQuery)
      ));
    return matches.slice(0, 50);
  }, [isPickerOpen, trimmedQuery, catalog]);

  const closePicker = useCallback(() => {
    setIsBrowseOpen(false);
    setQuery("");
  }, []);

  const handleToggleManagedFixture = useCallback(() => {
    if (!isDevManagedFixtureAvailable) return;
    setShowManagedFixture((current) => !current);
    setShowManagedPreview(false);
    setShowConfirm(false);
    setSaveError(null);
    setIsBrowseOpen(false);
    setQuery("");
  }, [isDevManagedFixtureAvailable]);

  // Portfolio/basket slices, shaped for the borrowed strategy utilities (which
  // are self-directed-only). Plutus Managed is intentionally excluded: it has no
  // catalog entry, minimum NLV, or contract remarks, so tradability and
  // minimum-investment do not apply to it.
  const selfDirectedDraft = useMemo<StrategyDraftEntry[]>(
    () =>
      rows
        .filter((r) => r.weight > 0 && r.entityType !== "MANAGED_STRATEGY")
        .map((r) => ({
          entityType: r.entityType as "PORTFOLIO" | "BASKET",
          productId: r.productId,
          weight: r.weight,
          displayName: r.displayName,
        })),
    [rows]
  );
  const selfDirectedSaved = useMemo<StrategyDraftEntry[]>(
    () =>
      savedRows
        .filter((r) => r.entityType !== "MANAGED_STRATEGY")
        .map((r) => ({
          entityType: r.entityType as "PORTFOLIO" | "BASKET",
          productId: r.productId,
          weight: r.weight,
          displayName: r.displayName,
        })),
    [savedRows]
  );

  // Products being added that the account cannot fully trade (warn-only).
  const restrictedNewProducts = useMemo(
    () => computeRestrictedNewProducts(selfDirectedDraft, selfDirectedSaved, productLookup),
    [selfDirectedDraft, selfDirectedSaved, productLookup]
  );

  // Current holdings shaped for the education gate's drift animation.
  const ackCurrentStrategies = useMemo<StrategyEntry[]>(
    () =>
      savedRows
        .filter((r) => r.entityType !== "MANAGED_STRATEGY")
        .map((r) => ({
          entityType: r.entityType as "PORTFOLIO" | "BASKET",
          productId: r.productId,
          weight: String(r.weight),
          position: "LONG" as const,
          driftedWeight: r.currentWeight,
        })),
    [savedRows]
  );

  // ---- Save gates, mirrored from StrategyDraftEditor (the source of truth) ----
  const isEmptyNoSaved = totalWeight === 0 && savedRows.length === 0;
  const isOverAllocationLimit = allocationLimit !== null
    && displayedTotalWeight > allocationLimit;
  const allocationLimitLabel = allocationLimit === null
    ? "--"
    : `${formatWeight(allocationLimit)}%`;
  // Products added (e.g. from Discover) that still sit at 0% are counted as a
  // pending change on Discover, but the save payload drops zero-weight rows, so
  // they are not yet saveable. Name that explicitly instead of the misleading
  // "No changes to save".
  const unweightedAdditions = useMemo(
    () => rows.filter((row) => row.weight <= 0 && !savedRows.some(
      (saved) => saved.entityType === row.entityType && saved.productId === row.productId
    )),
    [rows, savedRows]
  );

  // Below-minimum positions (recommendation, never a hard block). Carries the
  // allocation + minimum so the confirmation can name the shortfall per row.
  const belowMinDetails = useMemo(() => {
    if (!isCustomer || nlv == null) {
      return [] as Array<{ displayName: string; allocation: number; minNlv: number }>;
    }
    const out: Array<{ displayName: string; allocation: number; minNlv: number }> = [];
    for (const row of rows) {
      if (row.weight <= 0) continue;
      const minNlv = minNlvOf(row.entityType, row.productId);
      if (minNlv == null) continue;
      const allocation = weightToWholeDollars(row.weight, nlv);
      if (allocation >= minNlv) continue;
      out.push({ displayName: row.displayName, allocation, minNlv });
    }
    return out;
  }, [isCustomer, nlv, rows, minNlvOf]);

  // Account-info fetch failure: a customer whose NLV never resolved cannot have
  // minimum-investment checks run, so block the save rather than save against
  // unknown balances. Matches the legacy editor's guard exactly
  // (StrategyDraftEditor.tsx: isCustomer && !isLoadingAccount && nlv == null).
  const accountInfoUnavailable =
    isCustomer && !isLoadingAccount && nlv == null && status === "loaded";

  const isSaveDisabled =
    showManagedFixture ||
    !userConfigLoaded ||
    allocationLimit === null ||
    status !== "loaded" ||
    accountInfoUnavailable ||
    hasMultipleManagedRows ||
    isOverAllocationLimit ||
    isEmptyNoSaved ||
    managedSizing.blockedReason != null ||
    !isDirty ||
    isSaving;

  const saveDisabledReason = (): string | undefined => {
    if (showManagedFixture) return "Dev preview cannot be applied";
    if (!userConfigLoaded) return "Loading account limits";
    if (allocationLimit === null) return "Account type unavailable";
    if (status !== "loaded") return "Loading allocations";
    if (accountInfoUnavailable) return "Account information unavailable";
    if (hasMultipleManagedRows) return "Allocation editing is unavailable with multiple managed strategies";
    if (isOverAllocationLimit) return `Total weight exceeds ${allocationLimitLabel}`;
    if (isEmptyNoSaved) return "Add an allocation to continue";
    if (managedSizing.blockedReason) return managedSizing.blockedReason;
    if (!isDirty) {
      return unweightedAdditions.length > 0
        ? `Set a weight for ${unweightedAdditions.length === 1 ? "the product you added" : "the products you added"}`
        : "No changes to save";
    }
    return undefined;
  };

  const cashWeight = roundToDecimals(Math.max(0, 100 - displayedTotalWeight));
  const displayedTotalWithCash = roundToDecimals(displayedTotalWeight + cashWeight);
  const cashAmount = displayedAccountValue == null
    ? null
    : weightToWholeDollars(cashWeight, displayedAccountValue);
  const totalAmount = displayedAccountValue == null
    ? null
    : weightToWholeDollars(displayedTotalWithCash, displayedAccountValue);

  const addSearchResult = useCallback(
    (item: ProductSearchItem) => {
      if (hasMultipleManagedRows) return;
      addRow({
        entityType: item.productType,
        productId: item.productId,
        displayName: item.title,
      });
    },
    [addRow, hasMultipleManagedRows]
  );

  // Closing keeps the draft (products added on Discover, in-progress weights)
  // so the investor can come back and finish -- mirrors leaving the legacy
  // Strategy page. Discard is the explicit "throw it away" action.
  const handleClose = useCallback(() => {
    if (isSaving) return;
    onClose();
  }, [isSaving, onClose]);

  const handleDiscard = useCallback(() => {
    if (isSaving) return;
    resetToSaved();
    onClose();
  }, [isSaving, resetToSaved, onClose]);

  const handleApply = useCallback(async () => {
    if (hasMultipleManagedRows) return;
    if (allocationLimit === null || displayedTotalWeight > allocationLimit) {
      setSaveError(
        allocationLimit === null
          ? "Account type is unavailable. Reload your account before saving."
          : `Total weight exceeds ${allocationLimitLabel}. Reduce weights before saving.`
      );
      return;
    }
    setIsSaving(true);
    setSaveError(null);
    const payload = rows
      .filter((row) => row.weight > 0)
      .map((row) => ({
        entityType: row.entityType,
        productId: row.productId,
        weight: row.weight,
        position: "LONG" as const,
      }));
    try {
      await investorStrategyService.syncStrategies(payload);
      // Adopt the just-saved allocation as the draft's new baseline so the shared
      // draft (and Discover membership) reflect it. The sync response carries
      // only self-directed rows, so read the full records back. If that read
      // fails the save still succeeded, so invalidate instead of leaving a stale
      // baseline and a pre-save persisted draft behind.
      try {
        markSaved(await investorStrategyService.getStrategyRecords());
      } catch {
        invalidate();
      }
      setShowConfirm(false);
      onSaved?.();
      onClose();
    } catch (err) {
      // Surface the service error message rather than a generic string, so the
      // real reason is legible.
      setSaveError(err instanceof Error ? err.message : "Failed to save allocations. Please try again.");
    } finally {
      setIsSaving(false);
    }
  }, [
    allocationLimit,
    allocationLimitLabel,
    displayedTotalWeight,
    hasMultipleManagedRows,
    rows,
    markSaved,
    invalidate,
    onSaved,
    onClose,
  ]);

  if (!open) return null;

  const diff = computeAllocationDiff(
    normalizeManagedForDiff(rows),
    normalizeManagedForDiff(savedRows)
  );

  const renderRow = (row: AlphaAllocationRow, rowIndex: number) => {
    const rowLabel = !showManagedFixture
      && hasMultipleManagedRows
      && row.entityType === "MANAGED_STRATEGY"
      ? `${row.displayName} #${row.productId}`
      : row.displayName;
    const isManaged = row.entityType === "MANAGED_STRATEGY";
    const product = isManaged
      ? null
      : productLookup.get(`${row.entityType}-${row.productId}` as ProductLookupKey);
    const providerName = isManaged ? "Plutus" : product?.providerName ?? row.displayName;
    const providerId = isManaged ? null : product?.providerId ?? null;
    const progressClass = ALLOCATION_PROGRESS_CLASSES[
      rowIndex % ALLOCATION_PROGRESS_CLASSES.length
    ];
    const boundedProgress = Math.min(100, Math.max(0, row.weight));
    const rowEditingDisabled = hasMultipleManagedRows || showManagedFixture;
    const rowAmount = displayedAccountValue == null
      ? null
      : weightToWholeDollars(row.weight, displayedAccountValue);
    const minNlv = minNlvOf(row.entityType, row.productId);
    // Same per-position shortfall copy as the legacy editor (shared helper).
    const minNlvWarning =
      !showManagedFixture && isCustomer && nlv != null
        ? getMinNlvShortfallWarning(row.weight, nlv, minNlv)
        : null;
    const showManagedFloorAlert = !showManagedFixture
      && isManaged
      && managedSizing.belowFloor
      && managedSizing.managedWeightChanged
      && managedSizing.floorUsd != null;
    const rowKey = isManaged && !showManagedFixture && !hasMultipleManagedRows
      ? "MANAGED_STRATEGY-singleton"
      : `${row.entityType}-${row.productId}`;
    const managedFloorImpossible = showManagedFloorAlert
      && nlv != null
      && managedSizing.floorUsd != null
      && nlv < managedSizing.floorUsd;
    return (
      <div
        key={rowKey}
        data-testid={`alpha-alloc-row-${row.entityType}-${row.productId}`}
        className={`flex items-center gap-2 border-b border-v7-ink/12 py-3 ${ALLOC_GRID} lg:gap-6 lg:py-5`}
      >
        <div className="min-w-0 flex-1 lg:flex-none">
          <div className="flex min-w-0 items-start gap-2.5">
            <ProviderLogo
              providerId={providerId}
              providerName={providerName}
              srcOverride={isManaged ? "/favicon.png" : undefined}
              testId={`alpha-alloc-provider-logo-${row.entityType}-${row.productId}`}
            />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                {row.entityType !== "PORTFOLIO" && (
                  <span className={`shrink-0 rounded border px-2 py-0.5 text-[9px] font-semibold uppercase tracking-[0.09em] ${chipClass(row.entityType)}`}>
                    {CHIP_LABEL[row.entityType]}
                  </span>
                )}
                <span className="truncate text-sm font-medium text-v7-ink" title={rowLabel}>{rowLabel}</span>
                {isManaged && (showManagedFixture || !hasMultipleManagedRows) && (
                  <button
                    type="button"
                    onClick={() => setShowManagedPreview(true)}
                    className="ml-auto shrink-0 border-b border-v7-bronze-dark/40 text-[11px] font-medium text-v7-bronze-dark transition-colors hover:border-v7-ink hover:text-v7-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-v7-bronze-dark"
                  >
                    Details
                  </button>
                )}
              </div>
              <div
                role="progressbar"
                aria-label={`${rowLabel} allocation`}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={boundedProgress}
                aria-valuetext={`${formatWeight(row.weight)}% allocated`}
                data-progress-color={progressClass}
                data-testid={`alpha-alloc-progress-${row.entityType}-${row.productId}`}
                className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-v7-mist"
              >
                <span
                  className={`block h-full rounded-full ${progressClass}`}
                  style={{ width: `${boundedProgress}%` }}
                />
              </div>
            </div>
          </div>
          {minNlvWarning && (
            <p className="mt-1 text-[11px] text-v7-rust">{minNlvWarning}</p>
          )}
          {showManagedFloorAlert && managedSizing.floorUsd != null && (
            <p role="alert" className="mt-1.5 text-xs leading-relaxed text-v7-rust">
              {managedFloorImpossible
                ? `Plutus Managed needs at least ${formatCurrencyWithCommas(managedSizing.floorUsd, 0)} and your account is below that minimum.`
                : `Plutus Managed needs at least ${formatCurrencyWithCommas(managedSizing.floorUsd, 0)}${
                  managedSizing.floorWeightPct != null
                    ? ` (${managedSizing.floorWeightPct}% of your account)`
                    : ""
                }. Raise the allocation or remove Managed.`}
            </p>
          )}
          {!showManagedFixture && isManaged && managedSizing.rematchPending && (
            <p
              data-testid="alpha-managed-rematch-note"
              className="mt-1.5 text-xs leading-relaxed text-v7-bronze-dark"
            >
              This amount changes the managed strategy matched to your profile. You will review it before applying.
            </p>
          )}
          {!showManagedFixture && isManaged && managedSizing.resolutionFailed && (
            <div
              role="alert"
              data-testid="alpha-managed-rematch-error"
              className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs leading-relaxed text-v7-rust"
            >
              <span>We couldn&apos;t match your managed mix.</span>
              <button
                type="button"
                onClick={managedSizing.retryResolution}
                className="inline-flex items-center gap-1 font-semibold text-v7-ink-soft underline underline-offset-2 transition-colors hover:text-v7-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-v7-bronze-dark"
              >
                <RefreshCw className="h-3 w-3" aria-hidden="true" />
                Retry
              </button>
            </div>
          )}
        </div>
        <div
          className="hidden min-w-0 text-right lg:block"
          data-testid={`alpha-alloc-amount-${row.entityType}-${row.productId}`}
          aria-label={`${rowLabel} dollar allocation`}
        >
          <span className="font-v7-serif text-base tabular-nums text-v7-ink">
            {rowAmount == null ? USD_NULL_DISPLAY : formatCurrencyWithCommas(rowAmount, 0)}
          </span>
        </div>
        <div className="flex w-[100px] shrink-0 justify-end lg:w-auto">
          <AllocationWeightControl
            label={rowLabel}
            value={row.weight}
            disabled={rowEditingDisabled}
            onChange={(weight) => setWeight(row.entityType, row.productId, weight)}
          />
        </div>
        <div className="flex w-6 shrink-0 justify-end lg:w-auto">
          <button
            type="button"
            aria-label={`Remove ${rowLabel}`}
            disabled={rowEditingDisabled}
            onClick={() => removeRow(row.entityType, row.productId)}
            className="grid h-11 w-11 place-items-center rounded-v7-control border border-transparent text-v7-ink/40 transition-colors hover:border-v7-rust/40 hover:text-v7-rust disabled:cursor-not-allowed disabled:text-v7-ink/20"
          >
            <Trash2 className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      </div>
    );
  };

  return (
    <>
    <div
      className="fixed inset-0 z-50 flex animate-[alphaOverlayIn_0.2s_ease-out] bg-v7-ink/60 motion-reduce:animate-none sm:items-center sm:justify-center sm:p-4"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) handleClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="alpha-alloc-title"
        data-testid="alpha-allocations-modal"
        className="flex h-full w-full flex-col overflow-hidden bg-v7-white font-v7-sans animate-[alphaModalIn_0.24s_cubic-bezier(0.4,0,0.2,1)] motion-reduce:animate-none sm:h-auto sm:max-h-[92vh] sm:w-[92vw] sm:max-w-[960px] sm:rounded-v7-panel sm:border sm:border-v7-ink/10 sm:shadow-[0_24px_60px_rgba(17,29,50,0.35)] lg:h-[90vh] lg:max-h-[1040px] lg:w-[calc(100vw-3rem)] lg:max-w-[1480px] lg:rounded-v7-popover"
      >
        {/* Header */}
        <div
          data-testid="alpha-alloc-header"
          className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-2 border-b border-v7-ink/12 px-5 py-[calc(1rem+env(safe-area-inset-top))] sm:px-6 sm:py-5 lg:grid-cols-[minmax(0,1fr)_auto_auto] lg:gap-x-4 lg:px-8 lg:py-6"
        >
          <div
            data-testid="alpha-alloc-title-group"
            className="col-start-1 row-start-1 flex min-w-0 flex-col items-start gap-1 sm:flex-row sm:items-center sm:gap-5"
          >
            <h2 id="alpha-alloc-title" className="shrink-0 font-v7-serif text-2xl font-normal tracking-[-0.01em] text-v7-ink lg:text-[30px] lg:leading-8">
              Edit allocations
            </h2>
            <div className="min-w-0 sm:border-l sm:border-v7-ink/12 sm:pl-5" aria-live="polite">
              <span className="block text-[10px] font-semibold uppercase tracking-[0.1em] text-v7-ink/45">
                {showManagedFixture ? "Fixture account value" : "Account value"}
              </span>
              <span
                data-testid="alpha-alloc-account-value"
                className="ph-mask mt-0.5 block truncate font-v7-serif text-lg tabular-nums text-v7-ink lg:text-xl"
              >
                {!showManagedFixture && isLoadingAccount
                  ? "Loading"
                  : displayedAccountValue == null
                    ? USD_NULL_DISPLAY
                    : formatCurrencyWithCommas(displayedAccountValue, 0)}
              </span>
            </div>
          </div>
          {!showConfirm
            && !showManagedFixture
            && status === "loaded"
            && !hasMultipleManagedRows
            && managedRow == null && (
              <div className="col-span-2 row-start-2 max-w-full justify-self-end lg:col-span-1 lg:col-start-2 lg:row-start-1">
                <AlphaManagedNudge onAdopt={handleAdoptManaged} />
              </div>
            )}
          <button
            type="button"
            aria-label="Close"
            onClick={handleClose}
            className="col-start-2 row-start-1 grid h-9 w-9 shrink-0 place-items-center rounded-v7-control border border-v7-ink/15 text-v7-ink-soft transition-colors hover:border-v7-ink lg:col-start-3"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>

        {showConfirm ? (
          <div className="flex-1 overflow-y-auto px-5 py-5 sm:px-6 lg:px-8 lg:py-7">
            <h3 className="font-v7-serif text-lg text-v7-ink">Confirm changes</h3>
            <p className="mt-1 text-[13px] text-v7-ink-soft">
              By applying, all changes will be accepted and may result in rebalances. Review the changes below.
            </p>

            {managedSizing.rematchPending && (
              <div
                data-testid="alpha-managed-rematch-card"
                className="mt-4 rounded-v7-panel border border-v7-bronze/30 bg-v7-bronze/10 p-4"
              >
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 shrink-0 text-v7-bronze-dark" aria-hidden="true" />
                  <span className="text-sm font-semibold text-v7-ink">
                    Your managed mix will be updated
                  </span>
                </div>
                {savedManagedRow && nlv != null && managedSizing.dollars != null && (
                  <p className="mt-2 font-v7-serif text-sm text-v7-ink-soft">
                    <span className="text-v7-ink/45">
                      {formatCurrencyWithCommas(Math.round((savedManagedRow.weight / 100) * nlv), 0)}
                    </span>
                    {" managed "}&rarr;{" "}
                    <span className="text-v7-ink">
                      {formatCurrencyWithCommas(managedSizing.dollars, 0)} managed
                    </span>
                  </p>
                )}
                <p className="mt-2 text-xs leading-relaxed text-v7-ink-soft">
                  Plutus matches the mix to the dollars it manages. Because your managed amount
                  is changing, the underlying mix updates to the one matched for the new amount.
                  Your risk profile is unchanged. Applying may rebalance managed holdings.{" "}
                  <button
                    type="button"
                    onClick={() => setShowManagedPreview(true)}
                    className="font-semibold text-v7-bronze-dark hover:underline"
                  >
                    See the updated mix
                  </button>
                </p>
              </div>
            )}

            {restrictedNewProducts.length > 0 && (
              <div className="mt-4 rounded-v7-panel border border-v7-ink/15 bg-v7-paper p-4">
                <p className="text-sm font-semibold text-v7-ink">
                  {restrictedNewProducts.length === 1
                    ? "1 product you are adding is partly restricted for your account"
                    : `${restrictedNewProducts.length} products you are adding are partly restricted for your account`}
                </p>
                <ul className="mt-2 space-y-1">
                  {restrictedNewProducts.map((product) => (
                    <li
                      key={product.key}
                      className="border-b border-dashed border-v7-ink/15 py-1 text-sm text-v7-ink last:border-b-0"
                    >
                      {product.title}
                    </li>
                  ))}
                </ul>
                <p className="mt-2 text-xs leading-relaxed text-v7-ink-soft">
                  Restricted holdings will not be bought, so these products will track their published performance less closely. You can still proceed.{" "}
                  <button
                    type="button"
                    onClick={() => setRestrictedDetails(restrictedNewProducts)}
                    className="font-semibold text-v7-bronze-dark hover:underline"
                  >
                    See details
                  </button>
                </p>
              </div>
            )}

            {belowMinDetails.length > 0 && (
              <div className="mt-4 rounded-v7-panel border border-v7-bronze/30 bg-v7-bronze/10 p-4">
                <div className="mb-2 flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 shrink-0 text-v7-bronze-dark" aria-hidden="true" />
                  <span className="text-sm font-semibold text-v7-ink">
                    {belowMinDetails.length === 1
                      ? "1 position is below the minimum recommended investment"
                      : `${belowMinDetails.length} positions are below the minimum recommended investment`}
                  </span>
                </div>
                <ul className="mb-2 space-y-1">
                  {belowMinDetails.map((item, index) => (
                    <li
                      key={`${item.displayName}-${index}`}
                      className="flex items-center justify-between border-b border-dashed border-v7-bronze/30 py-1 last:border-b-0"
                    >
                      <span className="text-sm text-v7-ink">{item.displayName}</span>
                      <span className="font-v7-serif text-xs text-v7-ink-soft">
                        <span className="text-v7-bronze-dark">{formatCurrencyWithCommas(item.allocation, 0)}</span>
                        {" - min "}
                        {formatCurrencyWithCommas(item.minNlv, 0)}
                      </span>
                    </li>
                  ))}
                </ul>
                <p className="text-xs leading-relaxed text-v7-ink-soft">
                  Your allocation does not meet the minimum recommended amount for these products. Some positions may not be bought, which can cause tracking error. You can still proceed.
                </p>
              </div>
            )}

            {unweightedAdditions.length > 0 && (
              <div
                data-testid="alpha-alloc-unweighted-warning"
                className="mt-4 rounded-v7-panel border border-v7-bronze/30 bg-v7-bronze/10 p-4"
              >
                <div className="mb-2 flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 shrink-0 text-v7-bronze-dark" aria-hidden="true" />
                  <span className="text-sm font-semibold text-v7-ink">
                    {unweightedAdditions.length === 1
                      ? "1 product will not be added"
                      : `${unweightedAdditions.length} products will not be added`}
                  </span>
                </div>
                <ul className="mb-2 space-y-1">
                  {unweightedAdditions.map((row) => (
                    <li
                      key={`${row.entityType}-${row.productId}`}
                      className="flex items-center justify-between border-b border-dashed border-v7-bronze/30 py-1 last:border-b-0"
                    >
                      <span className="text-sm text-v7-ink">{row.displayName}</span>
                      <span className="font-v7-serif text-xs text-v7-ink-soft">No weight set</span>
                    </li>
                  ))}
                </ul>
                <p className="text-xs leading-relaxed text-v7-ink-soft">
                  These are still at 0%, so they will not be part of your allocation. Go back and set a weight to include them.
                </p>
              </div>
            )}

            <div className="mt-4 overflow-hidden rounded-v7-panel border border-v7-ink/15">
              {diff.length === 0 ? (
                <p className="px-4 py-4 text-sm text-v7-ink-soft">No changes to apply.</p>
              ) : (
                diff.map((item) => (
                  <div
                    key={item.key}
                    className="flex items-center justify-between gap-4 border-b border-v7-ink/10 px-4 py-3 last:border-b-0"
                  >
                    <span className="min-w-0">
                      <span className="block truncate text-sm text-v7-ink">{item.displayName}</span>
                      {item.key === MANAGED_DIFF_KEY && managedSizing.rematchPending && (
                        <span className="block text-[11px] text-v7-bronze-dark">
                          Mix updated for the new amount
                        </span>
                      )}
                    </span>
                    <span className="flex items-center gap-2 font-v7-serif text-sm">
                      <span className="text-v7-ink/45">{formatWeight(item.from)}%</span>
                      <span className="text-v7-ink/30">&rarr;</span>
                      <span
                        className={
                          item.kind === "removed"
                            ? "text-v7-rust"
                            : item.kind === "added"
                              ? "text-v7-success"
                              : "text-v7-ink"
                        }
                      >
                        {formatWeight(item.to)}%
                      </span>
                    </span>
                  </div>
                ))
              )}
            </div>
            {saveError && (
              <div className="mt-4 rounded-v7-panel border border-v7-rust/30 bg-v7-rust/5 p-3">
                <span className="text-sm text-v7-rust">{saveError}</span>
              </div>
            )}
            <div className="mt-5 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowConfirm(false)}
                disabled={isSaving}
                className="rounded-v7-control border border-v7-ink/25 bg-v7-white px-4 py-2.5 text-sm font-medium text-v7-ink-soft transition-colors hover:border-v7-ink disabled:opacity-60"
              >
                Back
              </button>
              <button
                type="button"
                onClick={() => void handleApply()}
                disabled={isSaving}
                className="inline-flex items-center gap-2 rounded-v7-control bg-v7-ink px-5 py-2.5 text-sm font-semibold text-v7-white transition-colors hover:bg-v7-ink-soft disabled:cursor-wait disabled:bg-v7-mist disabled:text-v7-ink/60"
              >
                {isSaving && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
                {isSaving ? "Applying" : "Apply changes"}
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* Toolbar */}
            <div className="relative px-5 pb-3 pt-4 sm:px-6 lg:px-8 lg:pb-4 lg:pt-5">
              <div className="flex gap-2.5">
                <div className="relative flex-1">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-v7-ink/40" aria-hidden="true" />
                  <input
                    value={query}
                    disabled={hasMultipleManagedRows || showManagedFixture}
                    onChange={(event) => setQuery(event.target.value)}
                    placeholder="Search portfolios and products"
                    aria-label="Search portfolios and products"
                    className="min-h-11 w-full rounded-v7-control border border-v7-ink/20 bg-v7-white py-2.5 pl-9 pr-3 text-sm text-v7-ink outline-none transition-colors placeholder:text-v7-ink/40 focus:border-v7-bronze-dark focus:ring-1 focus:ring-v7-bronze-dark disabled:cursor-not-allowed disabled:bg-v7-paper disabled:text-v7-ink/45"
                  />
                </div>
                <button
                  type="button"
                  disabled={hasMultipleManagedRows || showManagedFixture}
                  onClick={() => setIsBrowseOpen((prev) => !prev)}
                  aria-expanded={isBrowseOpen}
                  className={`inline-flex min-h-11 shrink-0 items-center gap-2 rounded-v7-control border px-3.5 text-[13px] font-semibold transition-colors ${
                    isBrowseOpen
                      ? "border-v7-bronze-dark bg-v7-bronze/10 text-v7-bronze-dark"
                      : "border-v7-ink/25 bg-v7-white text-v7-ink hover:border-v7-ink"
                  }`}
                >
                  <Grid2X2 className="h-4 w-4 text-v7-bronze-dark" aria-hidden="true" />
                  Browse Discover
                </button>
                {isDevManagedFixtureAvailable && (
                  <button
                    type="button"
                    data-testid="alpha-managed-fixture-toggle"
                    aria-pressed={showManagedFixture}
                    aria-label={showManagedFixture
                      ? "Exit Managed fixture"
                      : "Preview Managed fixture"}
                    onClick={handleToggleManagedFixture}
                    className={`hidden min-h-11 shrink-0 items-center gap-2 rounded-v7-control border px-3.5 text-[13px] font-semibold transition-colors lg:inline-flex ${
                      showManagedFixture
                        ? "border-v7-bronze-dark bg-v7-bronze/10 text-v7-bronze-dark"
                        : "border-v7-ink/20 bg-v7-paper text-v7-ink-soft hover:border-v7-ink"
                    }`}
                  >
                    <span className="rounded border border-v7-bronze-dark/30 px-1.5 py-0.5 text-[9px] uppercase tracking-[0.08em] text-v7-bronze-dark">
                      Dev
                    </span>
                    {showManagedFixture ? "Exit Managed fixture" : "Preview Managed fixture"}
                  </button>
                )}
              </div>
              {showManagedFixture && (
                <div
                  data-testid="alpha-managed-fixture-note"
                  className="mt-3 hidden items-center justify-between gap-4 border-y border-v7-bronze/25 bg-v7-bronze/5 px-3 py-2 text-xs text-v7-ink-soft lg:flex"
                >
                  <span>Dev-only fixture. Live allocations and account data are unchanged.</span>
                  <span className="font-medium text-v7-bronze-dark">Editing and apply are disabled</span>
                </div>
              )}
              {isPickerOpen && (
                <>
                  {/* Click-away layer so the picker closes like a popover. */}
                  <button
                    type="button"
                    aria-label="Close product picker"
                    tabIndex={-1}
                    onClick={closePicker}
                    className="fixed inset-0 z-[5] cursor-default"
                  />
                  <div className="absolute inset-x-5 top-[3.75rem] z-10 flex max-h-80 flex-col overflow-hidden rounded-v7-panel border border-v7-ink/15 bg-v7-white shadow-[0_18px_40px_rgba(17,29,50,0.22)] sm:inset-x-6 lg:inset-x-8">
                    <div className="flex items-center justify-between border-b border-v7-ink/10 px-4 py-2">
                      <span className="text-[10px] font-semibold uppercase tracking-[0.1em] text-v7-ink/50">
                        {trimmedQuery.length > 0 ? "Results" : "All products"}
                      </span>
                      <button
                        type="button"
                        onClick={closePicker}
                        className="text-[11px] font-medium text-v7-ink-soft transition-colors hover:text-v7-ink"
                      >
                        Done
                      </button>
                    </div>
                    <div className="overflow-y-auto">
                      {catalogResults.length === 0 ? (
                        <p className="px-4 py-3 text-sm text-v7-ink-soft">No matching products.</p>
                      ) : (
                        catalogResults.map((item) => {
                          const already = isInDraft(item.productType, item.productId);
                          return (
                            <div
                              key={`${item.productType}-${item.productId}`}
                              className="flex items-center gap-3 border-b border-v7-ink/10 px-4 py-2.5 last:border-b-0 hover:bg-v7-paper"
                            >
                              <ProviderLogo providerId={item.providerId} providerName={item.providerName} />
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2">
                                  <span className="truncate text-sm font-medium text-v7-ink">{item.title}</span>
                                  <span className={`shrink-0 rounded border px-1.5 py-0.5 text-[8px] font-semibold uppercase tracking-[0.09em] ${chipClass(item.productType)}`}>
                                    {CHIP_LABEL[item.productType]}
                                  </span>
                                </div>
                                <div className="truncate text-xs text-v7-ink-soft">{item.providerName}</div>
                              </div>
                              <button
                                type="button"
                                disabled={already}
                                onClick={() => addSearchResult(item)}
                                className="inline-flex shrink-0 items-center gap-1.5 rounded-v7-control bg-v7-ink px-3 py-1.5 text-xs font-semibold text-v7-white transition-colors hover:bg-v7-ink-soft disabled:bg-v7-mist disabled:text-v7-ink/50"
                              >
                                {already ? "Added" : <><Plus className="h-3 w-3" aria-hidden="true" />Add</>}
                              </button>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto px-5 pb-2 sm:px-6 lg:px-8">
              {!showManagedFixture && (status === "loading" || status === "idle") ? (
                <div className="flex items-center justify-center py-16" role="status">
                  <Loader2 className="h-6 w-6 animate-spin text-v7-ink/50" aria-hidden="true" />
                </div>
              ) : !showManagedFixture && status === "error" ? (
                <div className="my-6 flex items-center justify-between gap-4 rounded-v7-panel border border-v7-rust/30 bg-v7-rust/5 p-4">
                  <span role="alert" className="text-sm text-v7-rust">
                    We couldn&apos;t load your allocations.
                  </span>
                  <button
                    type="button"
                    onClick={reload}
                    className="inline-flex items-center gap-2 rounded-v7-control border border-v7-rust/40 bg-v7-white px-3 py-1.5 text-xs font-medium text-v7-rust transition-colors hover:bg-v7-rust/10"
                  >
                    <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
                    Retry
                  </button>
                </div>
              ) : !showManagedFixture && accountInfoUnavailable ? (
                <div className="my-6 rounded-v7-panel border border-v7-rust/30 bg-v7-rust/5 p-4">
                  <span role="alert" className="text-sm text-v7-rust">
                    We couldn&apos;t fetch account information, please try again. If issue persists contact support.
                  </span>
                </div>
              ) : (
                <>
                  {!showManagedFixture && hasMultipleManagedRows && (
                    <div
                      role="alert"
                      data-testid="alpha-multiple-managed-block"
                      className="my-4 rounded-v7-panel border border-v7-rust/30 bg-v7-rust/5 p-4"
                    >
                      <p className="text-sm font-semibold text-v7-rust">
                        Allocation editing is unavailable
                      </p>
                      <p className="mt-1 text-xs leading-relaxed text-v7-ink-soft">
                        This account has multiple managed strategies. They remain visible, but no allocation can be changed until the account is reconciled.
                      </p>
                    </div>
                  )}
                  <div className={`flex items-center gap-2 border-b border-v7-ink/18 py-2 text-[10px] font-semibold uppercase tracking-[0.1em] text-v7-ink/50 ${ALLOC_GRID} lg:mt-1 lg:gap-6 lg:py-3`}>
                    <span className="flex-1 lg:flex-none">Allocation</span>
                    <span className="hidden text-right lg:block">Dollar allocation</span>
                    <span className="w-[100px] shrink-0 text-right lg:w-auto">Allocation&nbsp;%</span>
                    <span className="w-6 shrink-0 lg:w-auto"></span>
                  </div>
                  {displayedRows.map(renderRow)}

                  {/* Cash (derived) */}
                  <div
                    data-testid="alpha-alloc-cash"
                    className={`flex items-center gap-2 border-b border-v7-ink/12 py-3 ${ALLOC_GRID} lg:gap-6 lg:py-5`}
                  >
                    <div className="min-w-0 flex-1 lg:flex-none">
                      <div className="flex items-center gap-2">
                        <span className="shrink-0 rounded border border-v7-ink/14 px-2 py-0.5 text-[9px] font-semibold uppercase tracking-[0.09em] text-v7-ink/55">
                          Cash
                        </span>
                        <span className="truncate text-sm font-medium text-v7-ink-soft">Uninvested</span>
                      </div>
                      <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-v7-mist">
                        <span className="block h-full rounded-full bg-v7-ink/25" style={{ width: `${Math.min(cashWeight, 100)}%` }} />
                      </div>
                    </div>
                    <span
                      data-testid="alpha-alloc-cash-amount"
                      className="hidden text-right font-v7-serif text-base tabular-nums text-v7-ink/55 lg:block"
                    >
                      {cashAmount == null ? USD_NULL_DISPLAY : formatCurrencyWithCommas(cashAmount, 0)}
                    </span>
                    <span className="w-[100px] shrink-0 text-right font-v7-serif text-sm text-v7-ink/55 lg:w-auto lg:text-base">{formatWeight(cashWeight)}%</span>
                    <span className="w-6 shrink-0 lg:w-auto"></span>
                  </div>

                  {/* Total */}
                  <div className={`flex items-center gap-2 border-t border-v7-ink/18 py-3 ${ALLOC_GRID} lg:gap-6 lg:py-5`}>
                    <span className="flex-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-v7-ink/55 lg:flex-none">
                      Total <span className="font-normal normal-case tracking-normal text-v7-ink/45">&middot; cap {allocationLimitLabel}</span>
                    </span>
                    <span
                      data-testid="alpha-alloc-total-amount"
                      className="hidden text-right font-v7-serif text-base tabular-nums text-v7-ink lg:block"
                    >
                      {totalAmount == null ? USD_NULL_DISPLAY : formatCurrencyWithCommas(totalAmount, 0)}
                    </span>
                    <span
                      data-testid="alpha-alloc-total"
                      className={`w-[100px] shrink-0 text-right font-v7-serif text-base lg:w-auto lg:text-lg ${isOverAllocationLimit ? "text-v7-rust" : "text-v7-ink"}`}
                    >
                      {formatWeight(displayedTotalWithCash)}%
                    </span>
                    <span className="w-6 shrink-0 lg:w-auto"></span>
                  </div>

                  {/* Warnings */}
                  <div className="space-y-1.5 py-3 text-right">
                    {!showManagedFixture && isOverAllocationLimit && (
                      <p className="text-xs text-v7-rust">
                        Total weight exceeds {allocationLimitLabel}. Reduce weights or remove products.
                      </p>
                    )}
                    {!showManagedFixture && isDirty && belowMinDetails.length > 0 && (
                      <p className="text-xs text-v7-bronze-dark">
                        {belowMinDetails.length === 1 ? "1 product is" : `${belowMinDetails.length} products are`} below the minimum recommended investment. You can still save, but some positions may not be bought, which can cause tracking error.
                      </p>
                    )}
                    {!showManagedFixture && rows.some((row) => row.weight === 0) && (
                      <p className="text-xs text-v7-ink/60">Products at 0% are excluded on save.</p>
                    )}
                  </div>

                </>
              )}
            </div>

            {/* Footer */}
            <div className="flex flex-col gap-3 border-t border-v7-ink/12 bg-v7-paper/50 px-5 pt-4 pb-[calc(1rem+env(safe-area-inset-bottom))] sm:flex-row sm:items-center sm:justify-between sm:px-6 sm:pb-4 lg:px-8 lg:py-5">
              <div className="text-[13px] text-v7-ink-soft">
                Invested <b className="font-semibold text-v7-ink">{formatWeight(roundToDecimals(displayedTotalWeight))}%</b>
                {" "}&middot; Cash <b className="font-semibold text-v7-ink">{formatWeight(cashWeight)}%</b>
              </div>
              <div className="flex gap-2.5">
                {!showManagedFixture && hasPendingChanges && !hasMultipleManagedRows && (
                  <button
                    type="button"
                    onClick={handleDiscard}
                    className="whitespace-nowrap rounded-v7-control px-3 py-2.5 text-sm font-semibold text-v7-ink-soft transition-colors hover:text-v7-rust"
                  >
                    Discard
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleClose}
                  className="flex-1 whitespace-nowrap rounded-v7-control border border-v7-ink/25 bg-v7-white px-4 py-2.5 text-sm font-semibold text-v7-ink-soft transition-colors hover:border-v7-ink sm:flex-none"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={isSaveDisabled}
                  title={saveDisabledReason()}
                  onClick={() => {
                    // First-save education gate: unacknowledged users see the
                    // allocation-methodology walkthrough before the confirm step.
                    if (!hasAcknowledgedAllocation) setShowAckGate(true);
                    else setShowConfirm(true);
                  }}
                  className="flex-1 whitespace-nowrap rounded-v7-control bg-v7-ink px-5 py-2.5 text-sm font-semibold text-v7-white transition-colors hover:bg-v7-ink-soft disabled:cursor-not-allowed disabled:bg-v7-mist disabled:text-v7-ink/60 sm:flex-none"
                >
                  Review &amp; apply
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>

      {/* Borrowed gates, rendered as siblings (outside the panel's clipped,
          transformed stacking context) so they overlay the editor correctly. */}
      <AllocationMethodologyAckModal
        isOpen={showAckGate}
        currentStrategies={ackCurrentStrategies}
        draftEntries={selfDirectedDraft}
        productLookup={productLookup}
        onClose={() => setShowAckGate(false)}
        onAcknowledged={() => {
          setShowAckGate(false);
          setShowConfirm(true);
        }}
        appearance="alpha"
      />

      <ProductTradabilityModal
        isOpen={restrictedDetails.length > 0}
        onClose={() => setRestrictedDetails([])}
        products={restrictedDetails}
        customerCountry={customerCountry}
        appearance="alpha"
      />

      <AlphaManagedPreviewModal
        open={showManagedPreview}
        onClose={() => setShowManagedPreview(false)}
        weightPct={displayedManagedRow?.weight ?? 0}
        amountUsd={showManagedFixture
          ? weightToWholeDollars(
            displayedManagedRow?.weight ?? 0,
            DEV_MANAGED_FIXTURE_ACCOUNT_VALUE
          )
          : managedSizing.dollars}
        strategyName={showManagedFixture
          ? DEV_MANAGED_FIXTURE_STRATEGY
          : managedSizing.perf.data?.managedStrategyTitle ?? PLUTUS_MANAGED_LABEL}
        perf={showManagedFixture
          ? { status: "idle", data: null }
          : managedSizing.perf}
        managedChangePending={showManagedFixture
          ? false
          : managedSizing.managedWeightChanged || managedSizing.rematchPending}
        rematchPending={showManagedFixture ? false : managedSizing.rematchPending}
        heldSameWindowReturnPct={showManagedFixture
          ? null
          : managedSizing.heldSameWindowReturnPct}
        blockedReason={showManagedFixture ? null : managedSizing.blockedReason}
        constituents={showManagedFixture ? undefined : managedConstituents}
        constituentsLoading={!showManagedFixture && (
          managedSizing.perf.status === "loading"
          || (
            managedSizing.perf.status === "ready"
            && managedSizing.perf.data?.managedStrategyId !== managedRow?.productId
          )
        )}
      />
    </>
  );
};

export { PLUTUS_MANAGED_LABEL };
export default AlphaAllocationsModal;
